/**
 * job-offers-store — localStorage CRUD helpers for:
 *   JobOffer     — a saved job posting
 *   CVxJobEntry  — N:M relation between a CV version and a job offer
 *
 * All operations are synchronous (localStorage).
 * When Supabase auth is added, these will be the offline-first layer.
 */

import type { ParsedJobOffer } from '../types';
import { earnPoints } from './credits-store';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface JobOffer {
  id: string;
  title: string;
  company: string;
  sourceUrl?: string;
  source: 'manual' | 'extension' | 'paste';
  rawText: string;
  parsedData?: ParsedJobOffer;
  isFavorite: boolean;
  dateAdded: string;   // ISO date string
  updatedAt?: string;  // ISO date string — trackle la dernière modification pour sync serveur
}

export interface CVxJobEntry {
  id: string;
  cvVersionId: string;
  jobOfferId: string;
  atsScore?: number;
  adaptedCvId?: string; // ID of the version created by adaptation, if any
  createdAt: string;    // ISO date string
}

// ─── Storage keys ────────────────────────────────────────────────────────────

const JOB_OFFERS_KEY = 'cv_job_offers';
const CVXJOB_KEY = 'cv_xjob_entries';

// ─── JobOffer CRUD ───────────────────────────────────────────────────────────

export function getJobOffers(): JobOffer[] {
  try {
    const raw = localStorage.getItem(JOB_OFFERS_KEY);
    return raw ? (JSON.parse(raw) as JobOffer[]) : [];
  } catch {
    return [];
  }
}

function saveJobOffers(offers: JobOffer[]): void {
  try {
    localStorage.setItem(JOB_OFFERS_KEY, JSON.stringify(offers));
  } catch { /* ignore */ }
}

export function addJobOffer(offer: Omit<JobOffer, 'id' | 'dateAdded'>): JobOffer {
  const offers = getJobOffers();
  // Deduplicate: if same title+company already exists, return existing without adding
  if (offer.title && offer.title !== 'Offre sans titre' && offer.title !== '') {
    const dup = offers.find(
      (o) => o.title === offer.title && o.company === (offer.company ?? ''),
    );
    if (dup) return dup;
  }
  const newOffer: JobOffer = {
    ...offer,
    id: `job-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    dateAdded: new Date().toISOString(),
  };
  offers.unshift(newOffer); // newest first
  saveJobOffers(offers);
  // Gamification: earn points for saving a job offer
  earnPoints('saveOffer');
  return newOffer;
}

export function updateJobOffer(id: string, updates: Partial<JobOffer>): void {
  const offers = getJobOffers();
  const idx = offers.findIndex((o) => o.id === id);
  if (idx === -1) return;
  offers[idx] = { ...offers[idx]!, ...updates };
  saveJobOffers(offers);
}

export function deleteJobOffer(id: string): void {
  saveJobOffers(getJobOffers().filter((o) => o.id !== id));
  // Also delete associated CVxJob entries
  saveCVxJobEntries(getCVxJobEntries().filter((e) => e.jobOfferId !== id));
}

export function toggleJobOfferFavorite(id: string): void {
  const offers = getJobOffers();
  const offer = offers.find((o) => o.id === id);
  if (!offer) return;
  updateJobOffer(id, { isFavorite: !offer.isFavorite });
}

// ─── CVxJob CRUD ─────────────────────────────────────────────────────────────

export function getCVxJobEntries(): CVxJobEntry[] {
  try {
    const raw = localStorage.getItem(CVXJOB_KEY);
    return raw ? (JSON.parse(raw) as CVxJobEntry[]) : [];
  } catch {
    return [];
  }
}

function saveCVxJobEntries(entries: CVxJobEntry[]): void {
  try {
    localStorage.setItem(CVXJOB_KEY, JSON.stringify(entries));
  } catch { /* ignore */ }
}

/**
 * Upsert a CVxJob entry for a (cvVersionId, jobOfferId) pair.
 * If an entry already exists, update it. Otherwise create a new one.
 */
export function upsertCVxJob(
  cvVersionId: string,
  jobOfferId: string,
  updates: Partial<Omit<CVxJobEntry, 'id' | 'cvVersionId' | 'jobOfferId' | 'createdAt'>>,
): CVxJobEntry {
  const entries = getCVxJobEntries();
  const existing = entries.find(
    (e) => e.cvVersionId === cvVersionId && e.jobOfferId === jobOfferId,
  );
  if (existing) {
    const updated = { ...existing, ...updates };
    saveCVxJobEntries(entries.map((e) => (e.id === existing.id ? updated : e)));
    return updated;
  }
  const newEntry: CVxJobEntry = {
    id: `xjob-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    cvVersionId,
    jobOfferId,
    createdAt: new Date().toISOString(),
    ...updates,
  };
  saveCVxJobEntries([...entries, newEntry]);
  return newEntry;
}

/** Get all CVxJob entries for a specific job offer */
export function getEntriesForJob(jobOfferId: string): CVxJobEntry[] {
  return getCVxJobEntries().filter((e) => e.jobOfferId === jobOfferId);
}

/** Get all CVxJob entries for a specific CV version */
export function getEntriesForCv(cvVersionId: string): CVxJobEntry[] {
  return getCVxJobEntries().filter((e) => e.cvVersionId === cvVersionId);
}

/** Best ATS score achieved for a given CV version across all offers */
export function getBestScoreForCv(cvVersionId: string): number | null {
  const scores = getEntriesForCv(cvVersionId)
    .map((e) => e.atsScore)
    .filter((s): s is number => s !== undefined && s !== null);
  return scores.length ? Math.max(...scores) : null;
}

/** Best ATS score achieved for a given job offer across all CV versions */
export function getBestScoreForJob(jobOfferId: string): { score: number; cvVersionId: string } | null {
  const entries = getEntriesForJob(jobOfferId).filter(
    (e): e is CVxJobEntry & { atsScore: number } => e.atsScore !== undefined,
  );
  if (!entries.length) return null;
  const best = entries.reduce((a, b) => (a.atsScore > b.atsScore ? a : b));
  return { score: best.atsScore, cvVersionId: best.cvVersionId };
}

// ─── ApplicationTracking ──────────────────────────────────────────────────────

export type ApplicationStatus = 'draft' | 'sent' | 'interview' | 'rejected' | 'offer';

export interface ApplicationTracking {
  id: string;
  jobOfferId: string;
  cvVersionId?: string;
  status: ApplicationStatus;
  notes: string;
  updatedAt: string;
}

const APPLICATIONS_KEY = 'cv_applications';

export function getApplications(): ApplicationTracking[] {
  try {
    const raw = localStorage.getItem(APPLICATIONS_KEY);
    return raw ? (JSON.parse(raw) as ApplicationTracking[]) : [];
  } catch {
    return [];
  }
}

function saveApplications(apps: ApplicationTracking[]): void {
  try {
    localStorage.setItem(APPLICATIONS_KEY, JSON.stringify(apps));
  } catch { /* ignore */ }
}

export function upsertApplication(
  jobOfferId: string,
  updates: Partial<Omit<ApplicationTracking, 'id' | 'jobOfferId' | 'updatedAt'>>,
): ApplicationTracking {
  const apps = getApplications();
  const existing = apps.find((a) => a.jobOfferId === jobOfferId);
  const now = new Date().toISOString();
  if (existing) {
    const updated = { ...existing, ...updates, updatedAt: now };
    saveApplications(apps.map((a) => (a.id === existing.id ? updated : a)));
    return updated;
  }
  const newApp: ApplicationTracking = {
    id: `app-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    jobOfferId,
    status: 'draft',
    notes: '',
    ...updates,
    updatedAt: now,
  };
  saveApplications([...apps, newApp]);
  return newApp;
}

export function removeApplication(jobOfferId: string): void {
  saveApplications(getApplications().filter((a) => a.jobOfferId !== jobOfferId));
}
